/**
 * Server-side dependency injection tokens
 *
 * These tokens are used to inject server-side services (like Redis, content managers)
 * into the Angular application during SSR. The actual implementations are provided
 * by the server module at runtime.
 */

import { InjectionToken, Type } from '@angular/core';
import { TranslationObject } from '@ngx-translate/core';
import { IAuthComponent } from '../interfaces/auth-component';
import { FAQS } from '../interfaces/faq';
import { InstallationDetailsRoot } from '../interfaces/installation';
import { SupportedDevices } from '../interfaces/supported-devices';

/**
 * Interface for Redis client service
 */
export interface RedisClient {
  getKey<T>(key: string): Promise<T | null>;
  setKey<T>(key: string, value: T, timeToLive?: number): Promise<boolean>;
}

/**
 * Interface for SEO Content Manager service
 */
export interface SeoContentManager {
  getSeoContent(host: string, language: string): unknown;
  isReady(): boolean;
}

/**
 * Interface for Cache Warmup Service
 */
export interface CacheWarmupService {
  getCache(host: string): unknown;
  getCustomerId(host: string): string | null;
  getS3Bucket(host: string): string | null;
  isReady(): boolean;
}

/**
 * Interface for FAQ content manager service
 */
export interface FaqContentManager {
  fetchFaqContent(
    s3Bucket: string,
    customerId: string,
    host: string,
    language: string,
    theme: string
  ): Promise<FAQS>;
}

/**
 * Interface for Installation content manager
 */
export interface InstallationContent {
  fetchInstallationContent(
    s3Bucket: string,
    customerId: string,
    host: string,
    language: string,
    theme: string
  ): Promise<InstallationDetailsRoot>;
}

/**
 * Interface for Language content manager
 */
export interface LanguageContent {
  fetchLanguage(
    s3Bucket: string,
    customerId: string,
    host: string,
    language: string,
    theme: string
  ): Promise<TranslationObject>;
}

/**
 * Interface for Supported Devices manager
 */
export interface SupportedDevicesManager {
  fetchSupportedDevicesContent(
    s3Bucket: string,
    customerId: string,
    host: string,
    language: string,
    theme: string
  ): Promise<SupportedDevices[]>;
}

/**
 * Token for injecting Redis client service
 */
export const REDIS_CLIENT_TOKEN = new InjectionToken<RedisClient>('REDIS_CLIENT');

/**
 * Token for injecting FAQ content manager
 */
export const FAQ_CONTENT_TOKEN = new InjectionToken<FaqContentManager>('FAQ_CONTENT');

/**
 * Token for injecting Installation content manager
 */
export const INSTALLATION_CONTENT_TOKEN = new InjectionToken<InstallationContent>(
  'INSTALLATION_CONTENT'
);

/**
 * Token for injecting Language content manager
 */
export const LANGUAGE_CONTENT_TOKEN = new InjectionToken<LanguageContent>('LANGUAGE_CONTENT');

/**
 * Token for injecting Supported Devices manager
 */
export const SUPPORTED_DEVICES_TOKEN = new InjectionToken<SupportedDevicesManager>(
  'SUPPORTED_DEVICES'
);

/**
 * Token for injecting SEO Content Manager (server-side)
 */
export const SEO_CONTENT_MANAGER_TOKEN = new InjectionToken<SeoContentManager>(
  'SEO_CONTENT_MANAGER'
);

/**
 * Token for injecting Cache Warmup Service (server-side)
 */
export const CACHE_WARMUP_SERVICE_TOKEN = new InjectionToken<CacheWarmupService>(
  'CACHE_WARMUP_SERVICE'
);

/**
 * Token for injecting Auth Component
 */
export const AUTH_COMPONENT_TOKEN = new InjectionToken<Type<IAuthComponent>>('AUTH_COMPONENT');
